import { cn, dialogs, Drawer, Menu, RemoteImage, SvgIcon, Tooltip, useMediaQuery, type MenuItem } from "@cafe/ui";
import { Captions, CaptionsOff, Check, LayoutGrid, Lock, LogOut, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShellBanners } from "@/features/billing/shell-banners";
import { NotificationBell } from "@/features/notifications/bell";
import { AiButton } from "@/features/ai/ai-button";
import { HelpButton } from "@/features/help/help-button";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import logo from "@/assets/brand/logo-black-sm.svg";
import { useBrand, useBrandName } from "@/shared/ui/brand";
import { BranchSelect, useBranchMenuItems } from "@/shared/ui/branch-switch";
import { findNav } from "@/app/nav";
import { ItemIcon } from "@/shells/item-icon";
import { authApi } from "@/features/auth/api";
import { deviceEntry } from "@/features/terminal/device";
import { useAuth } from "@/features/auth/store";
import { useMe } from "@/shared/hooks/session";
import { LOCALES, setLocale } from "@/shared/i18n";
import { useVisibleLaunch, useVisibleNav, type VisibleItem, type VisibleSection } from "./use-nav";

const TOPBAR_H = "h-14 md:h-[76px]";

/** Плитка рабочего экрана (касса, кухня): заливка основным цветом, подпись снизу; без тарифа — с замком. */
function LaunchTile({ item }: { item: VisibleItem }) {
  const { t } = useTranslation();
  return (
    <Tooltip content={t(item.label)} side="right">
      <Link
        to={item.path}
        aria-label={t(item.label)}
        className={cn(
          "relative flex w-[60px] flex-col items-center gap-1 rounded-lg py-2 text-[11px] leading-tight font-semibold text-white shadow-md transition-opacity hover:opacity-90",
          item.locked ? "bg-primary/50" : "bg-primary",
        )}
      >
        <ItemIcon icon={item.icon} />
        <span className="max-w-full truncate px-1">{t(item.short ?? item.label)}</span>
        {item.locked && <Lock className="absolute top-1 right-1 size-3" aria-label={t("common.locked")} />}
      </Link>
    </Tooltip>
  );
}

const RAIL_LABELS_KEY = "cafe.railLabels";

/** Подписи под иконками тёмной панели: показаны по умолчанию, выбор запоминается в браузере. */
function useRailLabels() {
  const [shown, setShown] = useState(() => {
    try {
      return localStorage.getItem(RAIL_LABELS_KEY) !== "off";
    } catch {
      return true;
    }
  });
  const toggle = () =>
    setShown((v) => {
      try {
        localStorage.setItem(RAIL_LABELS_KEY, v ? "off" : "on");
      } catch {
        /* ignore */
      }
      return !v;
    });
  return [shown, toggle] as const;
}

/** Figma «Sidebar-left»: тёмная колонка 84px: разделы (подсказка при наведении, подписи можно скрыть), внизу — рабочие экраны. */
function Rail({ launch, sections, active, onPick }: { launch: VisibleItem[]; sections: VisibleSection[]; active?: string; onPick: (s: VisibleSection) => void }) {
  const { t } = useTranslation();
  const [labels, toggleLabels] = useRailLabels();
  const toggleText = t(labels ? "shell.hideLabels" : "shell.showLabels");
  return (
    <nav aria-label={t("shell.sections")} className={cn("sticky top-[76px] hidden h-[calc(100dvh-76px)] w-[84px] shrink-0 flex-col items-center overflow-y-auto bg-rail py-6 md:flex", labels ? "gap-2 px-2" : "gap-4 px-4")}>
      {sections.map((s) => {
        const isActive = s.id === active;
        return (
          <Tooltip key={s.id} content={t(s.label)} side="right">
            <button
              type="button"
              aria-label={t(s.label)}
              aria-current={isActive ? "page" : undefined}
              onClick={() => onPick(s)}
              style={isActive ? { backgroundImage: "var(--gradient-primary)" } : undefined}
              className={cn(
                "flex shrink-0 flex-col items-center justify-center rounded-lg transition-colors",
                labels ? "w-full gap-1 px-1 py-2" : "size-[52px]",
                isActive ? "text-white" : "text-rail-fg hover:bg-white/10 hover:text-white",
              )}
            >
              <SvgIcon src={isActive ? s.icon.solid : s.icon.line} />
              {labels && <span className="line-clamp-2 w-full text-center text-[11px] leading-tight font-medium break-words hyphens-auto">{t(s.label)}</span>}
            </button>
          </Tooltip>
        );
      })}
      {/* Рабочие экраны и переключатель подписей прижаты к низу панели */}
      <div className="mt-auto flex flex-col items-center gap-4 pt-2">
        {launch.length > 0 && (
          <>
            {sections.length > 0 && <span className="h-px w-10 shrink-0 bg-white/15" aria-hidden />}
            {launch.map((i) => <LaunchTile key={i.path} item={i} />)}
          </>
        )}
        <Tooltip content={toggleText} side="right">
          <button
            type="button"
            aria-label={toggleText}
            aria-pressed={labels}
            onClick={toggleLabels}
            className="grid size-11 shrink-0 place-items-center rounded-lg text-rail-fg transition-colors hover:bg-white/10 hover:text-white"
          >
            {labels ? <CaptionsOff className="size-5" strokeWidth={1.75} aria-hidden /> : <Captions className="size-5" strokeWidth={1.75} aria-hidden />}
          </button>
        </Tooltip>
      </div>
    </nav>
  );
}

/** Figma «Sidebar» (224px): активный пункт — светлый фон и оранжевая полоса справа. */
function Subnav({ section }: { section?: VisibleSection }) {
  const { t } = useTranslation();
  if (!section) return null;
  return (
    <nav aria-label={t(section.label)} className="sticky top-[76px] hidden h-[calc(100dvh-76px)] w-[224px] shrink-0 flex-col gap-4 overflow-y-auto border-r border-border bg-surface py-6 lg:flex">
      {section.items.map((i) => (
        <NavLink
          key={i.path}
          to={i.path}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 border-r-4 px-[18px] py-3.5 text-sm leading-[1.4] transition-colors",
              isActive ? "border-primary bg-primary-soft/80 font-semibold text-primary" : "border-transparent text-muted hover:text-fg",
            )
          }
        >
          <ItemIcon icon={i.icon} />
          <span className="flex-1 truncate">{t(i.label)}</span>
          {i.locked && <Lock className="size-4 text-subtle" aria-label={t("common.locked")} />}
        </NavLink>
      ))}
    </nav>
  );
}

/** Tablet/mobile: пункты раздела — горизонтальные вкладки над контентом. */
function SubnavTabs({ section }: { section?: VisibleSection }) {
  const { t } = useTranslation();
  if (!section || section.items.length < 2) return null;
  return (
    <nav aria-label={t(section.label)} className="flex gap-2 overflow-x-auto border-b border-border bg-surface px-4 py-2 lg:hidden">
      {section.items.map((i) => (
        <NavLink
          key={i.path}
          to={i.path}
          className={({ isActive }) =>
            cn(
              "inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-4 text-sm whitespace-nowrap",
              isActive ? "border-primary bg-primary-soft font-semibold text-primary" : "border-border text-fg",
            )
          }
        >
          {t(i.label)}
          {i.locked && <Lock className="size-3.5 text-subtle" />}
        </NavLink>
      ))}
    </nav>
  );
}

/** Mobile: нижняя навигация (4 раздела + «Ещё»), с учётом safe-area. */
function BottomNav({ sections, active, onPick }: { sections: VisibleSection[]; active?: string; onPick: (s: VisibleSection) => void }) {
  const { t } = useTranslation();
  const [more, setMore] = useState(false);
  const primary = sections.length > 5 ? sections.slice(0, 4) : sections;
  const rest = sections.length > 5 ? sections.slice(4) : [];
  const moreActive = rest.some((s) => s.id === active);
  const btn = (isActive: boolean) => cn("flex min-w-0 flex-1 flex-col items-center gap-1 pt-2 pb-1.5 text-[11px] leading-tight", isActive ? "text-primary font-semibold" : "text-muted");
  return (
    <>
      <nav aria-label={t("shell.sections")} className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden">
        {primary.map((s) => (
          <button key={s.id} type="button" onClick={() => onPick(s)} className={btn(s.id === active)} aria-current={s.id === active ? "page" : undefined}>
            <SvgIcon src={s.id === active ? s.icon.solid : s.icon.line} />
            <span className="max-w-full truncate px-1">{t(s.label)}</span>
          </button>
        ))}
        {rest.length > 0 && (
          <button type="button" onClick={() => setMore(true)} className={btn(moreActive)}>
            <span className="grid size-6 place-items-center text-lg leading-none">⋯</span>
            <span>{t("nav.more")}</span>
          </button>
        )}
      </nav>
      <Drawer open={more} onOpenChange={setMore} title={t("nav.more")}>
        <div className="flex flex-col gap-5">
          {rest.map((s) => (
            <div key={s.id} className="flex flex-col gap-1">
              <p className="text-xs font-medium tracking-wide text-subtle uppercase">{t(s.label)}</p>
              {s.items.map((i) => (
                <Link key={i.path} to={i.path} onClick={() => setMore(false)} className="flex min-h-12 items-center gap-3 rounded-lg px-2 hover:bg-surface-2">
                  <ItemIcon icon={i.icon} className="text-muted" />
                  <span className="flex-1">{t(i.label)}</span>
                  {i.locked && <Lock className="size-4 text-subtle" />}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </Drawer>
    </>
  );
}

function initials(name?: string) {
  return (name ?? "?").split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

/** Figma «Dasboard / topbar»: логотип | разделитель | заголовок … филиал, профиль. */
function Topbar({ title, launch }: { title: string; launch: VisibleItem[] }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const brand = useBrand();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === "dark");
  const isMobile = useMediaQuery("(max-width: 767.98px)"); // как md:hidden у списка в шапке
  // Телефон: филиал выбирается в меню профиля, в шапке места нет
  const branchItems = useBranchMenuItems();

  const menu: MenuItem[] = [
    { type: "label", label: <span className="block max-w-56 truncate">{me.data?.name} · {me.data?.company.name}</span> },
    ...(isMobile && branchItems.length > 0 ? [...branchItems, { type: "separator" as const }] : []),
    ...LOCALES.map((l) => ({ label: l.label, icon: i18n.language === l.value ? <Check /> : <span className="size-4" />, onSelect: () => setLocale(l.value) })),
    { type: "separator" as const },
    {
      label: t("common.theme"),
      icon: dark ? <Sun /> : <Moon />,
      onSelect: () => {
        const next = !dark;
        setDark(next);
        document.documentElement.dataset.theme = next ? "dark" : "light";
        try {
          localStorage.setItem("cafe.theme", next ? "dark" : "light");
        } catch {
          /* ignore */
        }
      },
    },
    {
      label: t("common.logout"),
      icon: <LogOut />,
      danger: true,
      onSelect: () =>
        void dialogs.confirmDanger({
          title: t("common.logoutTitle"),
          description: t("common.logoutHint"),
          confirmText: t("common.logout"),
          onConfirm: () => authApi.logout().then(() => navigate(deviceEntry() ?? "/login")),
        }),
    },
  ];

  return (
    <header className={cn("sticky top-0 z-30 flex shrink-0 items-center justify-between gap-3 border-b border-border bg-surface px-4 max-[359px]:gap-2 max-[359px]:px-3 md:px-6", TOPBAR_H)}>
      <div className="flex min-w-0 shrink-0 items-center gap-3 sm:shrink md:gap-6">
        <Link to="/dashboard" aria-label={t("shell.home")} className="shrink-0">
          {brand?.logo ? (
            <RemoteImage image={brand.logo} sizes="160px" alt={brand.name ?? me.data?.company.name ?? ""} className="h-7 w-auto max-w-40 object-contain sm:h-8 md:h-11" />
          ) : brand?.name ? (
            <span className="block max-w-40 truncate text-lg font-bold text-primary md:text-2xl">{brand.name}</span>
          ) : (
            <img src={logo} width={117} height={44} alt={t("shell.logo")} className="h-7 w-auto max-[359px]:h-6 sm:h-8 md:h-11" />
          )}
        </Link>
        <span className="hidden h-11 w-px bg-border md:block" aria-hidden />
        <p className="hidden truncate text-base leading-normal font-semibold sm:block md:text-xl">{title}</p>
      </div>
      <div className="flex min-w-0 items-center justify-end gap-2 max-[359px]:gap-1 md:gap-6">
        {/* Телефон: тёмной панели нет — рабочие экраны одной кнопкой в шапке (несколько — меню), чтобы не наезжать на логотип */}
        {launch.length === 1 && launch[0] && (
          <Link to={launch[0].path} aria-label={t(launch[0].label)} title={t(launch[0].label)}
            className={cn("relative grid size-9 shrink-0 place-items-center rounded-full text-white md:hidden", launch[0].locked ? "bg-primary/50" : "bg-primary")}>
            <ItemIcon icon={launch[0].icon} className="size-5" />
            {launch[0].locked && <Lock className="absolute -top-0.5 -right-0.5 size-3 text-fg" aria-label={t("common.locked")} />}
          </Link>
        )}
        {launch.length > 1 && (
          <Menu
            trigger={
              <button type="button" aria-label={t("shell.workScreens")} title={t("shell.workScreens")} className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-white md:hidden">
                <LayoutGrid className="size-5" strokeWidth={1.75} aria-hidden />
              </button>
            }
            items={[
              { type: "label", label: t("shell.workScreens") },
              ...launch.map((i) => ({
                label: <span className="flex items-center gap-2">{t(i.label)}{i.locked && <Lock className="size-3.5 text-subtle" aria-label={t("common.locked")} />}</span>,
                icon: <ItemIcon icon={i.icon} className="size-4" />,
                onSelect: () => navigate(i.path),
              })),
            ]}
          />
        )}
        {/* Выбор филиала в шапке — с планшета; на телефоне он в меню профиля */}
        <div className="hidden min-w-0 md:block">
          <BranchSelect className="min-w-0 max-w-56" />
        </div>
        <AiButton />
        <HelpButton />
        <NotificationBell />
        <Menu
          trigger={
            <button type="button" aria-label={t("common.profile")} className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-soft text-sm font-semibold text-primary md:size-11">
              {initials(me.data?.name)}
            </button>
          }
          items={menu}
        />
      </div>
    </header>
  );
}

export function AdminShell() {
  const { t } = useTranslation();
  const sections = useVisibleNav();
  const launch = useVisibleLaunch();
  const location = useLocation();
  const navigate = useNavigate();
  const me = useMe();
  const { branchId, setBranch } = useAuth();
  const match = findNav(location.pathname);
  const active = sections.find((s) => s.id === match?.section.id);

  // Устаревший/чужой филиал из localStorage → филиал по умолчанию
  useEffect(() => {
    if (me.data && !me.data.branches.some((b) => b.id === branchId)) setBranch(me.data.defaultBranchId);
  }, [me.data, branchId, setBranch]);

  const brandName = useBrandName();
  useEffect(() => {
    document.title = match ? `${t(match.item.label)} · ${brandName}` : brandName;
  }, [match, t, brandName]);

  const pick = (s: VisibleSection) => navigate(s.items[0]!.path);

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-full focus:bg-surface focus:px-4 focus:py-2">
        {t("shell.skipToContent")}
      </a>
      <Topbar title={match ? t(match.item.label) : brandName} launch={launch} />
      <ShellBanners />
      <div className="flex min-h-0 flex-1">
        <Rail launch={launch} sections={sections} active={active?.id} onPick={pick} />
        <Subnav section={active} />
        <main id="main" className="min-w-0 flex-1 pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0">
          <SubnavTabs section={active} />
          <div className="mx-auto w-full max-w-[1280px] p-4 sm:p-6">
            <Outlet />
          </div>
        </main>
      </div>
      <BottomNav sections={sections} active={active?.id} onPick={pick} />
    </div>
  );
}
